'use client';

import {
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Calendar from '@fluentui/svg-icons/icons/calendar_empty_20_regular.svg';
import ErrorCircle from '@fluentui/svg-icons/icons/error_circle_20_filled.svg';
import { useLocale } from 'next-intl';
import { FieldError } from 'react-hook-form';
import { cx } from '@/lib/utils';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  leading?: string | React.ReactNode;
  trailing?: string | React.ReactNode;
  maxLength?: number;
  value?: string | number;
  containerClassName?: string;
  messages?: string | string[];
  error?: FieldError | boolean;
  errorType?: string;
  noLocale?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  noTrailingError?: boolean;
  messagesClassName?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const TextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      value,
      leading,
      onChange,
      messages,
      trailing,
      max,
      maxLength,
      containerClassName,
      inputClassName,
      noLocale,
      error,
      errorType,
      labelClassName,
      noTrailingError,
      messagesClassName,
      ...otherProps
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);

    const [touched, setTouched] = useState(
      errorType === 'manual' ? true : false,
    );

    const touchedError = !!error && touched;

    const locale = useLocale();

    const containerClasses = `
    relative
    h-14
    w-full
  `.trim();

    const inputBaseClasses = `
    absolute h-full inset-0 w-full text-md font-normal leading-6 rounded-xl outline-none p-4 border
    ${
      touchedError
        ? 'border-danger focus:ring-danger focus:border-danger ltr:pr-[40px] rtl:pl-[40px]'
        : 'border-gray-300 focus:border focus:border-blue-300 focus:ring-blue-300'
    }
    ${Boolean(label) ? 'pt-7' : ''}
    ${trailing ? (locale === 'en' ? 'pr-8' : 'pr-4') : ''}
    ${
      leading
        ? `motion-safe:transition-[padding-left,padding-right] motion-safe:duration-200
           ${
             typeof leading === 'string' || typeof leading === 'object'
               ? `${
                   noLocale ? 'pl-10' : 'rtl:pr-10 ltr:pl-10'
                 } motion-safe:ease-in-out ${
                   noLocale ? 'pl-10' : 'ltr:focus:pl-10 rtl:focus:pr-10'
                 } [&:not(:placeholder-shown)]:pl-10`
               : ''
           }`
        : ''
    }
    ${noLocale ? 'text-left' : ''}
    bg-white text-gray-900
    body-large peer
    transition-colors duration-200
  `;

    const inputClasses = cx(
      inputBaseClasses,
      inputClassName,
      // otherProps.disabled && "!bg-gray-200 !opacity-50 disabled:!bg-gray-200 disabled:!text-gray-900",
    );

    const disabledOverlayClass = otherProps.disabled ? 'relative' : '';

    const containerClassesFinal = cx(containerClasses, containerClassName);

    // Define reusable class name constants
    const peerFocusClasses = `
    peer-focus:top-2 ${
      noLocale
        ? 'left-[13px]'
        : 'rtl:peer-focus:right-[13px] ltr:peer-focus:left-[13px]'
    }
    peer-focus:text-xs peer-focus:font-medium peer-focus:px-1 peer-focus:gap-0.5 peer-focus:text-gray-500 peer-focus:font-normal
  `.trim();

    const peerNotPlaceholderShownClasses = `
    peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:!text-xs peer-[:not(:placeholder-shown)]:!font-normal peer-[:not(:placeholder-shown)]:!text-gray-900 
    peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-medium rtl:peer-[:not(:placeholder-shown)]:!right-[13px] ltr:peer-[:not(:placeholder-shown)]:!left-[13px]
    peer-[:not(:placeholder-shown)]:px-1 peer-[:not(:placeholder-shown)]:gap-0.5
  `.trim();

    // Update labelClasses (removed peer-autofill classes)
    const labelClasses = `
    absolute ${
      noLocale ? 'left-4' : 'ltr:left-4 rtl:right-4'
    } top-2 font-normal leading-[12px] flex text-md gap-1 rounded-[0.625rem] text-gray-500 pointer-events-none bg-[transparent]
    motion-safe:transition-[top,right,left,font-size,font-weight,background-color_50ms] motion-safe:duration-200
    motion-safe:ease-in-out
    ${peerFocusClasses}
    ${peerNotPlaceholderShownClasses}
     ${
       leading
         ? noLocale
           ? 'peer-focus:pl-7 peer-[:not(:placeholder-shown)]:pl-7'
           : 'ltr:peer-focus:pl-7 rtl:peer-focus:pr-7 ltr:peer-[:not(:placeholder-shown)]:pl-7 rtl:peer-[:not(:placeholder-shown)]:pr-7'
         : ''
     }
    /* When the input is empty */
    peer-placeholder-shown:top-4 ${
      noLocale
        ? 'left-4'
        : 'ltr:peer-placeholder-shown:right-4 rtl:peer-placeholder-shown:left-4'
    }
  `.trim();

    const leadingClasses = `
    pointer-events-none absolute flex h-full items-center justify-center px-4 text-gray-700
    ${
      Boolean(label)
        ? 'opacity-0 peer-focus:opacity-100 peer-[:not(:placeholder-shown)]:opacity-100'
        : ''
    } 
    motion-safe:transition-opacity
    ${noLocale ? 'left-0 rtl:flex-row-reverse' : 'ltr:left-0 rtl:right-0'}
  `.trim();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      if (otherProps.type === 'number' || otherProps.type === 'text') {
        const num = String(val);
        if (maxLength && num.length > maxLength) {
          return;
        }
      }
      onChange?.(e);
    };

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        otherProps.onBlur?.(e);
      },
      [otherProps.onBlur],
    );

    useEffect(() => {
      if (
        typeof error === 'object' &&
        'type' in error &&
        error?.type === 'manual'
      ) {
        setTouched(true);
      }
    }, [error]);

    return (
      <div className="flex w-full flex-col">
        <div className={cx(containerClassesFinal, disabledOverlayClass)}>
          <input
            ref={inputRef}
            {...otherProps}
            disabled={otherProps.disabled}
            id={name}
            name={name}
            value={value}
            max={maxLength}
            placeholder={otherProps.placeholder || ' '}
            maxLength={maxLength}
            onChange={handleChange}
            className={cx(inputClasses, inputClassName)}
            lang={otherProps.type === 'number' ? 'en' : undefined}
            onBlur={handleBlur}
          />
          {otherProps.disabled && (
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gray-100/50" />
          )}
          {leading && (
            <div className={leadingClasses}>
              {typeof leading === 'string' ? (
                <p className="text-xs font-normal text-gray-700">{leading}</p>
              ) : (
                leading
              )}
            </div>
          )}
          <label htmlFor={name} className={cx(labelClasses, labelClassName)}>
            <p className={otherProps.disabled ? 'text-gray-500' : ''}>
              {label}
            </p>
          </label>
          {trailing && (
            <div
              className={`text-black-900 absolute flex h-full items-center gap-x-1.5 px-4 ${
                noLocale ? 'right-0' : 'ltr:right-0 rtl:left-0'
              }`}
            >
              {((noTrailingError && !touchedError) || !noTrailingError) && (
                <p className="mt-[2px] text-sm font-medium">{trailing}</p>
              )}

              {touchedError && <ErrorCircle className="fill-danger" />}
            </div>
          )}

          {error && touched && !trailing && (
            <div
              className={`pointer-events-none absolute flex h-full items-center gap-x-1.5 px-4 ${
                noLocale ? 'right-0' : 'ltr:right-0 rtl:left-0'
              }`}
            >
              {error && <ErrorCircle className="fill-danger" />}
            </div>
          )}

          {otherProps.type === 'date' && (
            <div className="absolute right-4 top-[25%] z-50 max-h-max max-w-max bg-white">
              <div
                className="cursor-pointer"
                onClick={() => inputRef.current?.showPicker()}
              >
                <Calendar />
              </div>

              {error && touched && <ErrorCircle className="fill-danger" />}
            </div>
          )}
        </div>
        {touched && messages && (
          <ul
            className={cx('px-4 pt-1 text-xs text-danger', messagesClassName)}
          >
            {(Array.isArray(messages) ? messages : [messages]).map(
              (message, index) => (
                <li key={index}>{message}</li>
              ),
            )}
          </ul>
        )}
      </div>
    );
  },
);

export default TextField;
